import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Bot, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardCopilot() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    setMessages([{
      role: 'assistant',
      content: lang === 'en'
        ? 'Hello! I\'m your AI co-pilot. I can help you analyze bookings, suggest marketing strategies, manage customers, and optimize your workshop operations. What do you need?'
        : 'Ciao! Sono il tuo co-pilota AI. Posso aiutarti ad analizzare le prenotazioni, suggerire strategie di marketing, gestire i clienti e ottimizzare le operazioni dell\'officina. Di cosa hai bisogno?'
    }]);
  }, [navigate, lang]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: input }]);
    const msg = input;
    setInput('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/copilot/chat`, { message: msg, session_id: sessionId }, { headers: { Authorization: `Bearer ${token}` } });
      if (!sessionId) setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Errore nella comunicazione');
    } finally {
      setLoading(false);
    }
  };

  const suggestions = lang === 'en'
    ? ['How many bookings this week?', 'Marketing ideas for this month', 'How to increase reviews?', 'Analyze customer trends']
    : ['Quante prenotazioni questa settimana?', 'Idee marketing per questo mese', 'Come aumentare le recensioni?', 'Analizza i trend clienti'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-copilot">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#0066b2] to-[#005494] rounded-2xl flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>AI Co-Pilot</h1>
              <p className="text-gray-600">{lang === 'en' ? 'Your intelligent business assistant' : 'Il tuo assistente business intelligente'}</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden flex flex-col" style={{ height: '600px' }}>
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="copilot-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`copilot-msg-${idx}`}>
                  <div className={`max-w-[85%] p-4 rounded-2xl ${msg.role === 'user' ? 'bg-[#0066b2] text-white rounded-br-none' : 'bg-gray-50 border border-gray-200 text-gray-800 rounded-bl-none'}`}>
                    <p className="text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-2xl rounded-bl-none">
                    <Loader2 className="w-5 h-5 animate-spin text-[#0066b2]" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-6 pb-2">
                <p className="text-xs text-gray-500 mb-2">{lang === 'en' ? 'Quick suggestions:' : 'Suggerimenti rapidi:'}</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => { setInput(s); }} className="px-3 py-1.5 bg-blue-50 text-[#0066b2] text-xs rounded-full border border-blue-100 hover:bg-blue-100 transition-colors" data-testid={`suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') handleSend(); }} placeholder={lang === 'en' ? 'Ask your co-pilot...' : 'Chiedi al tuo co-pilota...'} className="flex-1" disabled={loading} data-testid="copilot-input" />
                <Button onClick={handleSend} disabled={loading || !input.trim()} className="bg-[#0066b2] hover:bg-[#005494]" data-testid="copilot-send">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
